
import $ from 'jquery';
import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import { GroupSurveyExtension, GroupSurveyExtensionUiClient, SurveyJsUiClient, SurveyResultMeta } from '../../backend/backendapi';
import CenterOnParent from '../../util/CenterOnParent';
import { VueGoodTable } from 'vue-good-table';
import 'vue-good-table/dist/vue-good-table.css';
import { paginationOptions, searchOptions } from '../../../util/vue-good-table';
import { initClient } from '../../../api';
import { blobDownload, formatDateTime } from '../../../util';
import Spinner from '../../util/Spinner';
import GroupResultsModalFooter from './GroupResultsModal/GroupResultsModalFooter';

@Component({
  components: {
    CenterOnParent,
    GroupResultsModalFooter,
    Spinner,
    VueGoodTable,
  },
})
export default class GroupResultsModal extends Vue {
  group: GroupSurveyExtension | null = null;
  results: SurveyResultMeta[] | null = null;
  loading = false;
  downloadingCertificate: number[] = [];

  private readonly surveyApi = initClient(SurveyJsUiClient);
  private readonly groupSurveyExtensionApi = initClient(GroupSurveyExtensionUiClient);

  get columnsConfig(): any[] {
    return [{
      label: this.$t('survey.group.resultsModal.list.userName').toString(),
      field: (e: SurveyResultMeta) => (e.userFirstname ?? '') + ' ' + (e.userLastname ?? ''),
    }, {
      label: this.$t('survey.surveyResultMeta.model.surveyName').toString(),
      field: (e: SurveyResultMeta) => e.surveyName,
    }, {
      label: this.$t('survey.group.resultsModal.list.lastUndertaking').toString(),
      // e.date is nullable, but somehow the openapi generator does not properly generate the spec
      // also: if returned value is undefined, vgt does not render the cell at all, so we return null here in case the date is undefined
      field: (e: SurveyResultMeta) => e.date ?? null,
      type: 'date',
      formatFn: (e: Date | undefined) => !e ? this.$t('general.never').toString() : formatDateTime(e),
    }, {
      label: this.$t('survey.surveyResultMeta.model.passed').toString(),
      field: (e: SurveyResultMeta) => e.passed,
      type: 'boolean',
      formatFn: (value: boolean) => this.$t(value ? 'yes' : 'no').toString(),
    }, {
      label: this.$t('survey.surveyResultMeta.model.validTil').toString(),
      type: 'date',
      field: (e: SurveyResultMeta) => e.validTil,
      formatFn: (e: Date | undefined) => !e ? '' : formatDateTime(e),
    }, {
      label: this.$i18n.t('survey.group.resultsModal.list.actions').toString(),
      field: 'actions',
      width: '5em',
    }];
  }

  get loadingFailed(): boolean {
    return this.results === null;
  }

  get paginationOptions(): any {
    return paginationOptions(this.$i18n);
  }

  get searchOptions(): any {
    return searchOptions(this.$i18n);
  }

  public open(group: GroupSurveyExtension): void {
    this.group = group;
    this.reloadResults();
    $(this.$refs.modal!).modal('show');
  }

  public dismiss(): void {
    $(this.$refs.modal!).modal('hide');
  }

  async downloadCertificate(meta: SurveyResultMeta): Promise<void> {
    if (!meta.resultId) {
      return;
    }
    
    this.downloadingCertificate.push(meta.resultId);
    try {
      const file = await this.surveyApi.getResultCertificate(meta.surveyId, meta.resultId);
      blobDownload(file.data, file.fileName!);
    } finally {
      const index = this.downloadingCertificate.findIndex(e => e === meta.resultId);
      if (index >= 0) {
        this.downloadingCertificate.splice(index, 1);
      }
    }
  }

  async reloadResults(): Promise<void> {
    this.loading = true;
    try {
      this.results = null;
      if (!this.group) {
        return;
      }
      this.results = (await this.groupSurveyExtensionApi.getMostRecentResults(this.group.id))
          .sort((l, r) => (r.date?.getTime() ?? 0) - (l.date?.getTime() ?? 0)); // sort desc by date
    } finally {
      this.loading = false;
    }
  }
}
